import { WX_ID_TIME_SCOPE_COMPOSITE_RADAR } from "../../../store/WeatherContentsState";
import WRAP from "WRAP-JS";
import TimeController from "../helper/TimeController";
import MissingDataObserver from "../helper/MissingDataObserver";
import * as ConfigRadar from "./config/ConfigRadar";
import { configRadarAmedas, expoRadarLayerConfig, getLayerConfig } from "./config/ConfigTimeCompositeRadar";
import {
  COMMUNICATE_ERROR_MESSAGE_JMA_NOWCAST_AMEDAS_RADAR,
  COMMUNICATE_ERROR_MESSAGE_JMA_NOWCAST_RADAR,
  COMMUNICATE_ERROR_MESSAGE_JMA_OBS_RADAR,
  COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
} from "../../../constants/ErrorMessage";
import { nearValidTime } from "../helper/MapUtil";
import { translation } from '../../../utils/FPEUtils';
import { RADAR_TYPE_JMA_PRCINT_NAME } from "./Radar";
import LegendRadar from "../legend/LegendRadar";

// 気象庁高解像度レーダー
export const RADAR_TYPE_JMA_OBS = 'RadarJmaObs'
// 降水ナウキャスト
export const RADAR_TYPE_JMA_NOWCAST = 'RadarJmaNowcast'
// レーダーアメダス
export const RADAR_TYPE_JMA_NOWCAST_AMEDAS = 'RadarJmaNowcastAmedas';
// 実況のみ欠測判定を行う
const MISSING_THRESHOLD = {
  [RADAR_TYPE_JMA_OBS]: 1800,
  [RADAR_TYPE_JMA_NOWCAST]: 0,
  [RADAR_TYPE_JMA_NOWCAST_AMEDAS]: 0,
}

class TimeScopeCompositeRadar {
  static NAME = WX_ID_TIME_SCOPE_COMPOSITE_RADAR;

  // 気象庁高解像度レーダー
  dhJmaObs;
  layerJmaObs;

  // 降水ナウキャスト
  dhJmaNowcast;
  layerJmaNowcast;

  // レーダーアメダス
  dhJmaNowcastAmedas;
  layerJmaNowcastAmedas;

  // 現在表示中のレーダー種別
  currentRadarType = null;

  // タイムコントローラー
  timeController;
  // タイムコントローラーのリスト
  timeList;
  timeListMap = new Map();

  // 欠測判定クラス
  missingDataObserver;

  // 自動更新フラグ
  autoRefresh = true;
  // DHの自動更新状態
  dhAutoUpdate = true;

  // 時制欄に表示中の時制
  validTime = '---'

  // コンテンツ表示状態
  visible = false;
  // コンテンツ準備状態
  ready = false;
  dataResolve = false;

  // カバレッジ表示
  coverage = true;
  // 透過率
  transmittance = 47;

  // プランETD(現状プラン連携モードがDEADルートのため使われることはない)
  etd;
  // プランスライダーから指定される経過時間(現状プラン連携モードがDEADルートのため使われることはない)
  elapsed = 0;
  // WxContentsDispatcherとの連携
  dispatcher;
  // DHごとのエラー状態格納
  errorMessageInfo = {
    [RADAR_TYPE_JMA_OBS]: null,
    [RADAR_TYPE_JMA_NOWCAST]: null,
    [RADAR_TYPE_JMA_NOWCAST_AMEDAS]: null,
  }
  // DHごとの欠測状態格納(実況のみ欠測判定を行うのでRADAR_TYPE_JMA_OBS以外はtrueになることはない)
  dataMissingInfo = {
    [RADAR_TYPE_JMA_OBS]: false,
    [RADAR_TYPE_JMA_NOWCAST]: false,
    [RADAR_TYPE_JMA_NOWCAST_AMEDAS]: false,
  }

  constructor() {
    this.layerJmaObs = new WRAP.Geo.Layer(RADAR_TYPE_JMA_OBS);
    this.layerJmaNowcast = new WRAP.Geo.Layer(RADAR_TYPE_JMA_NOWCAST);
    this.layerJmaNowcastAmedas = new WRAP.Geo.Layer(RADAR_TYPE_JMA_NOWCAST_AMEDAS);

    this.layerJmaObs.setVisible(false);
    this.layerJmaNowcast.setVisible(false);
    this.layerJmaNowcastAmedas.setVisible(false);

    this.layerJmaObs.disableInteraction(true);
    this.layerJmaNowcast.disableInteraction(true);
    this.layerJmaNowcastAmedas.disableInteraction(true);

    this.timeController = new TimeController(WX_ID_TIME_SCOPE_COMPOSITE_RADAR, {
      label: 'Radar(actual-forecast)',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, MISSING_THRESHOLD, this);
  }

  async initialize() {
    await this.initData().then(()=>{
      this.dataResolve = true;
    }).catch(()=>{
      this.dataResolve = false;
    }).finally(()=>{
      this.initLayer();
      this.initTimeController();
      this.ready = true;
    });
  }

  initData() {
    let status = 'pending';
    // JMA_OBS_RADAR_PRCINT(実況のみ欠測判定を行う)
    this.dhJmaObs = new WRAP.Data(ConfigRadar.configRadarPrcintDh);
    this.missingDataObserver.monitoring(RADAR_TYPE_JMA_OBS, this.dhJmaObs, ()=>{return {visible:()=>this.visible}}, {callback: this._updateMissing.bind(this)});
    // JMA_NOWCAS_PRCINT_HRES
    this.dhJmaNowcast = new WRAP.Data(ConfigRadar.configRadarDhNowCast);
    // JMA_NOWCAS_RADAR_AMEDAS_PRCRIN
    this.dhJmaNowcastAmedas = new WRAP.Data(configRadarAmedas);

    const promises = [];

    const isResolved = () => status === 'fulfilled' && this.dhJmaObs.retained.validtime && this.dhJmaNowcast.retained.validtime && this.dhJmaNowcastAmedas.retained.validtime;

    const dataDefine = [
      {
        type: RADAR_TYPE_JMA_OBS,
        dh: this.dhJmaObs,
        layer: this.layerJmaObs,
        errorMessage: COMMUNICATE_ERROR_MESSAGE_JMA_OBS_RADAR
      },
      {
        type: RADAR_TYPE_JMA_NOWCAST,
        dh: this.dhJmaNowcast,
        layer: this.layerJmaNowcast,
        errorMessage: COMMUNICATE_ERROR_MESSAGE_JMA_NOWCAST_RADAR
      },
      {
        type: RADAR_TYPE_JMA_NOWCAST_AMEDAS,
        dh: this.dhJmaNowcastAmedas,
        layer: this.layerJmaNowcastAmedas,
        errorMessage: COMMUNICATE_ERROR_MESSAGE_JMA_NOWCAST_AMEDAS_RADAR
      }
    ];
    dataDefine.forEach(({type, dh, layer, errorMessage}) => {
      dh.validate((displayTime, data, content) => {
        return this._validateTime(type, data, displayTime, content);
      });

      promises.push(
        new Promise((resolve, reject) => {
          dh.updated('validtime', ref => {
            if (isResolved()) {
              this._selectValidTime(ref, type);
            }
            resolve(ref);
          });
          // validtimeのapiがエラーを返却しないので、エラーハンドリング用に別途timelist apiを実行
          dh.updated('timelist', () => {
            if (this.errorMessageInfo[type]) {
              this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo[type]});
              this.errorMessageInfo[type] = null;
            }
          }, () => {
            this.errorMessageInfo[type] = this.dispatcher({
              type: 'SHOW_ERROR', payload: {
                message: errorMessage,
                additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
              }
            });
            reject()
          });
        })
      );
    });

    return Promise.allSettled(promises).then(values => {
      // データの取得正否には関わらず、statusは'fulfilled'に更新
      status = 'fulfilled';
      for (const [i, v] of values.entries()) {
        if (i === 0 && v.status === 'fulfilled') {
          // 実況データを指定
          this._selectValidTime(v.value, RADAR_TYPE_JMA_OBS);
          break;
        } else if (v.status === 'rejected') {
          // 仮に自動更新で復帰しても再度drawLayerするまで描画することはできないので、自動更新は停止させる（停止させないと回復時にアラートが消え、コンテンツが描画されたとミスリードするため）
          dataDefine[i].dh.setAutoUpdate('validtime', false);
        }
      }
      const errors = values.filter(result => result.status === 'rejected');
      if (errors.length > 0) {
        return Promise.reject('One or more promises failed');
      }
    });
  }

  resolveData(visible) {
    if (this.dataResolve) {
      return Promise.resolve()
    }
    if (!visible) {
      this._setAutoUpdate(false);
      return Promise.reject()
    } else {
      const promises =[];
      for (const dh of [this.dhJmaObs, this.dhJmaNowcast, this.dhJmaNowcastAmedas]){
        if (!dh.retained.validtime) {
          promises.push(new Promise((resolve, reject) => {
            dh.load('validtime', null, true, (ref)=>{
              if (dh.retained.validtime) {
                resolve(dh.retained.validtime)
              } else {
                reject();
              }
            }, reject);
          }));
        }
      }

      return Promise.all(promises).then(() => {
        this.dataResolve = true
        this._selectValidTime(this.dhJmaObs.retained.validtime, RADAR_TYPE_JMA_OBS)
      });
    }
  }

  initLayer() {
    this.layerJmaObs.configure(this.dhJmaObs, getLayerConfig(this.layerJmaObs.name()));
    this.layerJmaNowcast.configure(this.dhJmaNowcast, getLayerConfig(this.layerJmaNowcast.name()));
    this.layerJmaNowcastAmedas.configure(this.dhJmaNowcastAmedas, getLayerConfig(this.layerJmaNowcastAmedas.name()));
  }

  initTimeController() {
    this.timeController.addLayer(this.layerJmaObs);
    this.timeController.addLayer(this.layerJmaNowcast);
    this.timeController.addLayer(this.layerJmaNowcastAmedas);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }

    await this.resolveData(visible).then(() => {
      this.setVisible(visible);
    });
  }

  getLayer() {
    return [
      this.layerJmaObs,
      this.layerJmaNowcast,
      this.layerJmaNowcastAmedas,
    ]
  }

  setVisible(visible) {
    if (!this.ready) {
      return;
    }

    const target = this._getRadarWithType(this.currentRadarType);
    if (target.layer.visible() !== visible) {
      if (visible) {
        target.layer.show();
      } else {
        target.layer.hide();
      }
    }

    const enabled = visible && this.autoRefresh;
    if (this.dhAutoUpdate !== enabled) {
      this._setAutoUpdate(enabled);
      this.dhAutoUpdate = enabled;
    }

    this._updateTimeList();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getTimeInformation() {
    return {
      id: TimeScopeCompositeRadar.NAME,
      label: 'Radar(actual-forecast)',
      validTime: {
        id: this.currentRadarType,
        time: this.validTime
      },
      baseTime: {},
      baseTimeFormatter: ()=>{
        // baseTime欄を利用して表示中のRadar名を表示
        const target = this._getRadarWithType(this.currentRadarType)
        return translation(target.name)
      }
    }
  }

  getLegend() {
    return {
      id: TimeScopeCompositeRadar.NAME,
      label: 'Radar',
      grouping: 'radar',
      component: LegendRadar
    }
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this.ready) return;

    const target = this._getRadarWithType(this.currentRadarType);

    this._selectValidTime(target.dh.retained.validtime);
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this.ready) return;

    const target = this._getRadarWithType(this.currentRadarType);
    this._selectValidTime(target.dh.retained.validtime);
  }

  getTimeController() {
    return this.timeController;
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) {
      return;
    }

    this.autoRefresh = autoFlg;

    if (!this.ready) {
      return;
    }
    this._setAutoUpdate(this.autoRefresh);
    this.dhAutoUpdate = this.autoRefresh;
  }

  getCoverage() {
    return this.coverage;
  }

  setCoverage(coverage) {

    if (this.coverage === coverage) {
      return;
    }
    this.coverage = coverage;

    const style = coverage ? 'default' : 'clear_coverage';
    this.layerJmaObs.setStyle(style);
    this.layerJmaNowcast.setStyle(style);
    this.layerJmaNowcastAmedas.setStyle(style);
  }

  getTransmittance() {
    return this.transmittance;
  }

  setTransmittance(transmittance) {
    if (this.transmittance === transmittance) {
      return;
    }
    this.transmittance = transmittance;
    const opacity = Math.round(255 / 100 * (100 - this.transmittance));

    if (this.ready) {
      for (const layer of [this.layerJmaObs, this.layerJmaNowcast, this.layerJmaNowcastAmedas]) {
        layer._conf.Attributes.style.default.palette.slice(2, 255).forEach(palette => {
          palette[3] = opacity;
        });
        layer.invalidate();
      }
    } else {
      expoRadarLayerConfig.Attributes.style.default.palette.slice(2, 255).forEach(palette => {
        palette[3] = opacity;
      });
    }
  }

  _getRadarWithType(type) {
    if (type === RADAR_TYPE_JMA_OBS) {
      return {
        dh: this.dhJmaObs,
        layer: this.layerJmaObs,
        name: RADAR_TYPE_JMA_PRCINT_NAME
      };
    } else if (type === RADAR_TYPE_JMA_NOWCAST) {
      return {
        dh: this.dhJmaNowcast,
        layer: this.layerJmaNowcast,
        name: 'NOWCAST'
      };
    } else if (type === RADAR_TYPE_JMA_NOWCAST_AMEDAS) {
      return {
        dh: this.dhJmaNowcastAmedas,
        layer: this.layerJmaNowcastAmedas,
        name: 'Radar AMeDAS'
      };
    }
  }

  /**
   * 自動更新モード時のvalid選択
   */
  _selectValidTime(ref, dataType) {
    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      if (!this.autoRefresh) {
        //タイムコントローラ操作時にレーダーを追加した場合、タイムコントローラの選択時制
        targetTime = this.timeController.getCurrentTime();
      } else {
        targetTime = Date.now();
      }
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    this._updateTimeList();

    // 自動更新のときは実況データを描画。ただし実況データが欠測時は予測データも描画対象にする
    let vt;
    if (dataType === RADAR_TYPE_JMA_OBS && !this.dataMissingInfo[RADAR_TYPE_JMA_OBS]) {
      vt = nearValidTime(ref.validtime, targetTime);
    } else if (this.dataMissingInfo[RADAR_TYPE_JMA_OBS]) {
      vt = nearValidTime(this.timeList, targetTime);
    }
    if (vt && vt !== this.validTime) {
      this.validTime = vt;
      this._updateDataset();
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  /**
   * タイムコントロールモード時のvalid選択
   */
  _validateTime(dataType, data, displayTime, content) {
    const vt = nearValidTime(this.timeList, displayTime);
    this.validTime = vt;
    if (this.currentRadarType === this.timeListMap.get(vt)) {
      content.validtime = vt;
      return true;
    } else {
      // 表示対象のレーダー変更
      this._updateDataset();
      return true;
    }
  }

  _updateTimeList() {
    // 3つのDHの時制を組み合わせてtimeListを作成
    const obsValidTimes = this.dhJmaObs?.retained?.validtime?.validtime ?? [];
    const nowcastValidTimes = this.dhJmaNowcast?.retained?.validtime?.validtime ?? [];
    const nowcastAmedasValidTimes = this.dhJmaNowcastAmedas?.retained?.validtime?.validtime ?? [];

    if (!obsValidTimes.length || !nowcastValidTimes.length || !nowcastAmedasValidTimes.length) return;

    // 1時間前 ≦ valid time < 現在時刻：気象庁高解像度レーダー
    // 最新から1時間以内のものにfilter
    let validTimes = Array.from(obsValidTimes).sort();
    let obsLatest;
    if (!this.dataMissingInfo[RADAR_TYPE_JMA_OBS]) {
      obsLatest = validTimes[validTimes.length - 1];
    } else {
      // 欠測している場合は現在時刻を基準にする
      obsLatest = new Date();
    }
    const latestTime = new WRAP.Core.DateTime(obsLatest);
    this.timeList = validTimes.reduce((acc, vt) => {
      const diff = latestTime.diff(new WRAP.Core.DateTime(vt))
      if (-3600 <= diff) {
        acc.push(vt);
        this.timeListMap.set(vt, RADAR_TYPE_JMA_OBS);
      }
      return acc
    }, [])

    // 気象庁高解像度レーダーのlatest < valid time < 1時間先：降水ナウキャスト
    // 気象庁高解像度レーダーから未来方向に1時間追加
    Array.from(nowcastValidTimes).sort().forEach(vt => {
      const diff = latestTime.diff(new WRAP.Core.DateTime(vt));
      if (0 < diff && diff <= 3600) {
        this.timeList.push(vt);
        this.timeListMap.set(vt, RADAR_TYPE_JMA_NOWCAST);
      }
    });

    // 降水ナウキャストの最新予測 < valid time < 6時間先：レーダーアメダス
    // 降水ナウキャストの最新予測からレーダーアメダスの最新予測
    const nowCastLatest = new WRAP.Core.DateTime(this.timeList[this.timeList.length -1]);
    Array.from(nowcastAmedasValidTimes).sort().forEach(vt => {
      const diff = nowCastLatest.diff(new WRAP.Core.DateTime(vt));
      if (0 < diff) {
        this.timeList.push(vt);
        this.timeListMap.set(vt, RADAR_TYPE_JMA_NOWCAST_AMEDAS);
      }
    });

    if (this.autoRefresh) {
      if (!this.dataMissingInfo[RADAR_TYPE_JMA_OBS]) {
        this.timeController.setCurrentTime(obsLatest);
      } else {
        this.timeController.setCurrentTime(nearValidTime(this.timeList, Date.now()));
      }
    }
    this.timeController.adjustCurrentTime();
    this.timeController.invalidate();
  }

  _getTimeListWithType(type) {
    const timeList = [];
    for (const [vt, _type] of this.timeListMap) {
      if (_type === type) {
        timeList.push(vt);
      }
    }
    return timeList
  }

  _updateMissing(dataType, missing) {
    const prev = this.dataMissingInfo[dataType];
    if (prev !== missing) {
      this.dataMissingInfo[dataType] = missing;
    }
  }

  _updateDataset() {
    // validTimeの該当するデータ
    const radarType = this.timeListMap.get(this.validTime);

    const targetRadar = this._getRadarWithType(radarType);

    // 表示対象のレーダーが変わった場合、元々表示していたレーダーをhideして表示対象をshowする
    if (this.currentRadarType !== radarType) {
      const prevRadarType = this.currentRadarType;
      this.currentRadarType = radarType;
      if (prevRadarType === null) {
        // 初期表示のデータセット
        this._applyValidTimeToLayer(targetRadar, this.validTime);
      } else  {
        if (!targetRadar.layer.visible()) {
          /*
           show()することで内部的に_validateTimeがもう一度呼ばれ、validTimeが切り替わるので
           このルートでlayerにvalidTimeをセットする処理は不要
           */
          targetRadar.layer.show();
        }

        //元々表示していたレーダーをhide (showの後にhideをしたほうがチラつき軽減)
        const prevTarget = this._getRadarWithType(prevRadarType);
        prevTarget.layer.hide();
      }
    } else {
      // 表示対象のレーダー変更なし
      this._applyValidTimeToLayer(targetRadar, this.validTime);
    }
  }

  _applyValidTimeToLayer(targetRadar, validTime) {
    targetRadar.layer.set({
      validtime: validTime
    });
  }

  _setAutoUpdate(flg) {
    this.dhJmaObs.setAutoUpdate('validtime', flg);
    this.dhJmaNowcast.setAutoUpdate('validtime', flg);
    this.dhJmaNowcastAmedas.setAutoUpdate('validtime', flg);
  }
}
export default TimeScopeCompositeRadar
